<template>
    <div class="credit-picker">
        <el-popover v-model="visible" trigger="click" placement="bottom-start" :width="width" :disabled="disabled" popper-class="credit-picker__popper" @show="onShow">
            <div ref="menu" class="credit-picker__menu" role="listbox" tabindex="-1" :aria-label="title || ariaLabel" :aria-activedescendant="activeIndex !== -1 ? optionId(activeIndex) : null" @keydown="onMenuKeydown">
                <div v-if="title" class="credit-picker__title">{{title}}</div>
                <div class="credit-picker__columns" :style="{'grid-template-columns': 'repeat(' + columnsData.length + ', minmax(0, 1fr))'}">
                    <div v-for="(column, ci) in columnsData" :key="ci" class="credit-picker__column">
                        <div v-for="group in column" :key="group.key" class="credit-picker__group" role="group" :aria-label="group.label || null">
                            <div v-if="group.label" class="credit-picker__group-label">{{group.label}}</div>
                            <div v-for="option in group.options" :key="option.index" :id="optionId(option.index)" role="option" :aria-selected="isSelected(option) ? 'true' : 'false'"
                                 class="credit-picker__option" :class="{'is-active': option.index === activeIndex, 'is-selected': isSelected(option)}"
                                 @click="select(option)" @mousemove="activeIndex = option.index">
                                <span v-if="option.markerClass" class="credit-picker__marker badge" :class="option.markerClass" aria-hidden="true">{{option.markerText}}</span>
                                <span class="credit-picker__label">{{option.label}}</span>
                                <span class="credit-picker__check" aria-hidden="true">✓</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <button ref="toggle" slot="reference" type="button" class="credit-picker__toggle" :class="{'is-invalid': hasError, 'is-empty': !current}" :disabled="disabled"
                    aria-haspopup="listbox" :aria-expanded="visible ? 'true' : 'false'" :aria-label="ariaLabel ? ariaLabel + ': ' + (current ? current.label : placeholder) : null" @keydown="onToggleKeydown">
                <span v-if="current && current.markerClass" class="credit-picker__marker badge" :class="current.markerClass" aria-hidden="true">{{current.markerText}}</span>
                <span class="credit-picker__label">{{current ? current.label : placeholder}}</span>
                <span class="credit-picker__caret" aria-hidden="true"></span>
            </button>
        </el-popover>
    </div>
</template>

<script>
    // Выпадающий список полей кредитной заявки, нарисованный без системного select.
    // columns — колонки меню: [[{key, label, options: [{value, label, markerClass, markerText}]}]]
    export default {
        name: "CreditPicker",
        props: {
            value: {default: null},
            columns: {type: Array, default: () => []},
            title: {type: String, default: ''},
            ariaLabel: {type: String, default: ''},
            placeholder: {type: String, default: 'Не задано'},
            currentLabel: {type: String, default: ''},
            width: {type: Number, default: 240},
            disabled: {type: Boolean, default: false},
            hasError: {type: Boolean, default: false},
        },
        data: () => ({
            visible: false,
            activeIndex: -1,
        }),
        computed: {
            // Те же колонки, у каждого варианта сквозной index: сверху вниз по левой колонке, затем по правой
            columnsData() {
                let index = 0;
                return this.columns
                    .map(column => column
                        .map(group => Object.assign({}, group, {options: group.options.map(o => Object.assign({}, o, {index: index++}))}))
                        .filter(group => group.options.length))
                    .filter(column => column.length);
            },
            flatOptions() {
                return _.flatMap(this.columnsData, column => _.flatMap(column, group => group.options));
            },
            current() {
                if (this.value === null || this.value === undefined || this.value === '') return null;
                let option = _.find(this.flatOptions, o => o.value === this.value);
                if (option) return option;
                // Текущее значение может отсутствовать среди доступных вариантов (например, неактивный сотрудник)
                return this.currentLabel ? {value: this.value, label: this.currentLabel} : null;
            },
        },
        methods: {
            optionId(index) {
                return 'credit-picker-' + this._uid + '-' + index;
            },
            isSelected(option) {
                return option.value === this.value;
            },
            onShow() {
                let selected = _.find(this.flatOptions, o => this.isSelected(o));
                this.activeIndex = selected ? selected.index : (this.flatOptions.length ? 0 : -1);
                this.$nextTick(() => {
                    this.$refs.menu.focus({preventScroll: true});
                    this.scrollToActive();
                });
            },
            close() {
                this.visible = false;
                this.$refs.toggle.focus();
            },
            select(option) {
                this.close();
                if (this.isSelected(option)) return;
                this.$emit('input', option.value);
                this.$emit('change', option.value);
            },
            moveActive(step) {
                let count = this.flatOptions.length;
                if (!count) return;
                this.activeIndex = this.activeIndex === -1 ? 0 : Math.min(Math.max(this.activeIndex + step, 0), count - 1);
                this.scrollToActive();
            },
            // Переход в соседнюю колонку на ту же по счёту строку
            moveColumn(step) {
                let lists = this.columnsData.map(column => _.flatMap(column, group => group.options.map(o => o.index)));
                let ci = _.findIndex(lists, list => list.includes(this.activeIndex));
                let target = lists[ci + step];
                if (ci === -1 || !target) return;
                this.activeIndex = target[Math.min(lists[ci].indexOf(this.activeIndex), target.length - 1)];
                this.scrollToActive();
            },
            scrollToActive() {
                let el = this.activeIndex !== -1 ? document.getElementById(this.optionId(this.activeIndex)) : null;
                if (el) el.scrollIntoView({block: 'nearest'});
            },
            onToggleKeydown(e) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    this.visible = true;
                }
            },
            onMenuKeydown(e) {
                switch (e.key) {
                    case 'ArrowDown': this.moveActive(1); break;
                    case 'ArrowUp': this.moveActive(-1); break;
                    case 'ArrowRight': this.moveColumn(1); break;
                    case 'ArrowLeft': this.moveColumn(-1); break;
                    case 'Home': this.moveActive(-this.flatOptions.length); break;
                    case 'End': this.moveActive(this.flatOptions.length); break;
                    // Пробел не обрабатываем: его keyup после возврата фокуса снова открыл бы список
                    case 'Enter':
                        if (this.activeIndex !== -1) this.select(this.flatOptions[this.activeIndex]);
                        break;
                    // Escape закрывает только список, окно остаётся открытым
                    case 'Escape':
                    case 'Tab':
                        this.close();
                        break;
                    default:
                        return;
                }
                e.preventDefault();
                e.stopPropagation();
            },
        },
    }
</script>

<style scoped>
    .credit-picker__toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        min-width: 0;
        height: 34px;
        padding: 0 10px;
        border: 1px solid #dee2e6;
        border-radius: 7px;
        background: #fff;
        color: #3F4B57;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
    }
    .credit-picker__toggle:hover:not(:disabled) {
        border-color: #b5c0cc;
    }
    .credit-picker__toggle:focus {
        outline: none;
    }
    .credit-picker__toggle:focus-visible {
        outline: 2px solid #2c7be5;
        outline-offset: 2px;
    }
    .credit-picker__toggle[aria-expanded="true"] {
        border-color: #2c7be5;
        box-shadow: 0 0 0 2px rgba(44, 123, 229, .15);
    }
    .credit-picker__toggle:disabled {
        background: #f7f9fb;
        cursor: default;
    }
    .credit-picker__toggle.is-empty {
        color: #6c757d;
    }
    .credit-picker__toggle.is-invalid {
        border-color: #dc3545;
    }
    .credit-picker__toggle .credit-picker__label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .credit-picker__caret {
        flex: 0 0 7px;
        width: 7px;
        height: 7px;
        margin: -3px 2px 0 4px;
        border-right: 1.5px solid #6c757d;
        border-bottom: 1.5px solid #6c757d;
        transform: rotate(45deg);
    }
    .credit-picker__toggle:disabled .credit-picker__caret {
        visibility: hidden;
    }
    .credit-picker__toggle[aria-expanded="true"] .credit-picker__caret {
        margin-top: 3px;
        transform: rotate(-135deg);
    }
    .credit-picker__marker {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 18px;
        width: 18px;
        height: 18px;
        padding: 0;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
    }
    .credit-picker__menu {
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        outline: none;
    }
    .credit-picker__title {
        padding: 5px 9px 7px;
        margin-bottom: 4px;
        border-bottom: 1px solid #e9ecef;
        color: #6c757d;
        font-size: 11px;
        font-weight: 600;
    }
    .credit-picker__columns {
        display: grid;
        gap: 8px;
    }
    .credit-picker__group-label {
        padding: 6px 9px 3px;
        color: #6c757d;
        font-size: 11px;
        font-weight: 600;
    }
    .credit-picker__option {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 30px;
        padding: 4px 9px;
        border-radius: 6px;
        color: #3F4B57;
        font-size: 13px;
        cursor: pointer;
        user-select: none;
    }
    .credit-picker__option.is-active {
        background: #f0f5ff;
    }
    .credit-picker__option.is-selected {
        font-weight: 600;
    }
    .credit-picker__option .credit-picker__label {
        flex: 1 1 auto;
        min-width: 0;
    }
    .credit-picker__check {
        visibility: hidden;
        flex: 0 0 13px;
        color: #2c7be5;
        font-size: 14px;
        text-align: center;
    }
    .credit-picker__option.is-selected .credit-picker__check {
        visibility: visible;
    }
</style>

<style>
    .credit-picker__popper.el-popover {
        padding: 6px;
        border-radius: 12px;
    }
</style>
